import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, LogIn } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const LOGOS = {
  rrdc: "https://customer-assets.emergentagent.com/job_field-rental/artifacts/8kaelsop_Untitled%20design.png",
  turf_house: "https://customer-assets.emergentagent.com/job_field-rental/artifacts/nltdiuae_turf%20blog.png",
  riverview: "https://customer-assets.emergentagent.com/job_field-rental/artifacts/ov6p5t0e_Riverview%20Athletics%20Logo.png",
};

function getBranding(inquiryType, location) {
  // Anything at Turf House → Turf House logo ONLY, name "Turf House"
  if (location === "turf_house") {
    return { logo1: LOGOS.turf_house, logo2: null, logo3: null, name: "Turf House" };
  }
  // Rolling River field/rink/courts rental → Riverview + Rolling River logos
  if (inquiryType === "rental" && (location === "rrdc_field" || location === "rrdc_rink" || location === "rrdc_courts")) {
    return { logo1: LOGOS.riverview, logo2: LOGOS.rrdc, logo3: null, name: "Riverview Athletics LI" };
  }
  // Birthday, Picnic & RRDC events → Rolling River logo ONLY
  if (inquiryType === "birthday_party") return { logo1: LOGOS.rrdc, logo2: null, logo3: null, name: "Rolling River Day Camp" };
  if (inquiryType === "picnic") return { logo1: LOGOS.rrdc, logo2: null, logo3: null, name: "Rolling River Day Camp" };
  if (inquiryType === "event" && (location === "rrdc_field" || location === "rrdc_rink" || location === "rrdc_courts")) return { logo1: LOGOS.rrdc, logo2: null, logo3: null, name: "Rolling River Day Camp" };
  // Default (link preview before any selection) → ALL THREE brand logos
  return { logo1: LOGOS.rrdc, logo2: LOGOS.riverview, logo3: LOGOS.turf_house, name: "Rentals & Events" };
}

// RRDC Birthday Party Packages — pulled live from /api/venues so admin price/bullet edits are reflected here.

export default function InquiryFormPage() {
  const { setTheme } = useTheme();
  const isDark = false;
  const navigate = useNavigate();

  // Always force light mode on the inquiry form (for embeds)
  useEffect(() => { setTheme("light"); }, [setTheme]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [venues, setVenues] = useState([]);
  const [form, setForm] = useState({
    inquiry_type: "", location: "", first_name: "", last_name: "",
    street: "", city: "", state: "", zip_code: "", phone: "", email: "", organization: "",
    sport_activity: "", num_players: "", venue_option: "", num_guests: "", child_age: "",
    package_tier: "", rental_subtype: "",
    event_type: "", expected_attendance: "", catering_needs: "", picnic_type: "",
    requested_date: "", start_time: "", duration: "",
    lead_source: "", additional_notes: "",
  });

  useEffect(() => {
    api.get("/venues").then(({ data }) => setVenues(data.venues)).catch(() => {});
  }, []);

  // Fetch unavailable ranges for the selected date+location so we can grey-out conflicts in real time
  const [unavailable, setUnavailable] = useState([]);
  useEffect(() => {
    if (!form.requested_date || !form.location) {
      setUnavailable([]);
      return;
    }
    api.get(`/calendar/availability?calendar=${form.location}&date=${form.requested_date}`)
      .then(({ data }) => setUnavailable(data.unavailable || []))
      .catch(() => setUnavailable([]));
  }, [form.requested_date, form.location]);

  // Add minutes (HH:MM) helper
  const addHoursToTime = (time, hours) => {
    if (!time || !hours) return null;
    const [h, m] = time.split(":").map(Number);
    const total = h * 60 + m + Math.round(parseFloat(hours) * 60);
    const hh = String(Math.floor(total / 60) % 24).padStart(2, "0");
    const mm = String(total % 60).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  // Does range [start, end) overlap any unavailable block?
  const rangeConflicts = (start, end) => {
    if (!start || !end) return false;
    return unavailable.some((u) => u.start_time < end && u.end_time > start);
  };

  // Effective duration (birthday parties are 1.5h)
  const effectiveDuration =
    form.inquiry_type === "birthday_party"
      ? 1.5
      : form.duration
        ? parseFloat(form.duration)
        : null;
  const proposedEnd =
    form.start_time && effectiveDuration
      ? addHoursToTime(form.start_time, effectiveDuration)
      : null;
  const hasTimeConflict =
    form.requested_date && form.start_time && proposedEnd
      ? rangeConflicts(form.start_time, proposedEnd)
      : false;

  const set = (k, v) => {
    const updated = { ...form, [k]: v };
    if (k === "inquiry_type" && v === "birthday_party") updated.duration = "1.5";
    if (k === "inquiry_type" && v === "picnic") {
      updated.location = "rrdc_field";
      updated.picnic_type = "";
    }
    setForm(updated);
  };
  const branding = getBranding(form.inquiry_type, form.location);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.inquiry_type || !form.location || !form.first_name || !form.last_name || !form.phone || !form.email) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        num_players: form.num_players ? parseInt(form.num_players) : null,
        num_guests: form.num_guests ? parseInt(form.num_guests) : null,
        child_age: form.child_age ? parseInt(form.child_age) : null,
        expected_attendance: form.expected_attendance ? parseInt(form.expected_attendance) : null,
        duration: form.duration ? parseFloat(form.duration) : null,
      };
      await api.post("/inquiries", payload);
      setSubmitted(true);
      toast.success("Inquiry submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md border-border text-center">
          <CardContent className="p-8 space-y-4">
            <CheckCircle2 size={48} className="mx-auto text-emerald-500" />
            <h2 className="font-outfit text-2xl font-bold">Thank You!</h2>
            <p className="text-muted-foreground">Your inquiry has been submitted. We'll reach out within 24 hours.</p>
            <Button data-testid="submit-another" onClick={() => {
              setSubmitted(false);
              setForm({
                inquiry_type: "", location: "", first_name: "", last_name: "",
                street: "", city: "", state: "", zip_code: "", phone: "", email: "", organization: "",
                sport_activity: "", num_players: "", venue_option: "", num_guests: "", child_age: "",
                package_tier: "", rental_subtype: "",
                event_type: "", expected_attendance: "", catering_needs: "",
                requested_date: "", start_time: "", duration: "",
                lead_source: "", additional_notes: "",
              });
            }}>Submit Another Inquiry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="inquiry-form-page">
      {/* Top bar */}
      <div className="sticky top-0 z-20 w-full border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto flex items-center justify-between h-12 px-4">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center overflow-hidden shrink-0 ${isDark ? "bg-white" : "bg-transparent"}`}>
              <img src={branding.logo1} alt="" className="w-5 h-5 object-contain" />
            </div>
            {branding.logo2 && (
              <div className={`w-7 h-7 rounded-full flex items-center justify-center overflow-hidden shrink-0 ${isDark ? "bg-white" : "bg-transparent"}`}>
                <img src={branding.logo2} alt="" className="w-5 h-5 object-contain" />
              </div>
            )}
            {branding.logo3 && (
              <div className={`w-7 h-7 rounded-full flex items-center justify-center overflow-hidden shrink-0 ${isDark ? "bg-white" : "bg-transparent"}`}>
                <img src={branding.logo3} alt="" className="w-5 h-5 object-contain" />
              </div>
            )}
            <span className="text-sm font-medium text-muted-foreground font-outfit">{branding.name}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              data-testid="inquiry-login-btn"
              variant="ghost" size="sm"
              onClick={() => navigate("/login")}
              className="gap-1.5"
            >
              <LogIn size={16} /> Login
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto py-8 px-4">
        {/* Header with dynamic branding */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center overflow-hidden ${isDark ? "bg-white" : "bg-transparent"}`}>
              <img src={branding.logo1} alt={branding.name} className="w-12 h-12 object-contain" />
            </div>
            {branding.logo2 && (
              <div className={`w-16 h-16 rounded-full flex items-center justify-center overflow-hidden ${isDark ? "bg-white" : "bg-transparent"}`}>
                <img src={branding.logo2} alt="" className="w-12 h-12 object-contain" />
              </div>
            )}
            {branding.logo3 && (
              <div className={`w-16 h-16 rounded-full flex items-center justify-center overflow-hidden ${isDark ? "bg-white" : "bg-transparent"}`}>
                <img src={branding.logo3} alt="" className="w-12 h-12 object-contain" />
              </div>
            )}
          </div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold">{branding.name}</h1>
          <p className="text-muted-foreground text-sm mt-1">Submit an inquiry to get started</p>
        </div>

        <Card className="border-border">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Inquiry Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Inquiry Type *</label>
                  <Select value={form.inquiry_type} onValueChange={(v) => {
                    set("inquiry_type", v);
                    // Reset location if switching to/from rental: birthday/event/picnic
                    // only allow rrdc_field or turf_house, while rental allows the
                    // 4 specific facilities. Avoid stale rink/courts on a birthday inquiry.
                    if (v !== "rental" && (form.location === "rrdc_rink" || form.location === "rrdc_courts")) {
                      set("location", "rrdc_field");
                    }
                  }}>
                    <SelectTrigger data-testid="inquiry-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rental">Rental</SelectItem>
                      <SelectItem value="birthday_party">Birthday Party</SelectItem>
                      <SelectItem value="picnic">Picnic</SelectItem>
                      <SelectItem value="event">Event</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.inquiry_type !== "picnic" ? (
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Location *</label>
                    <Select value={form.location} onValueChange={(v) => set("location", v)}>
                      <SelectTrigger data-testid="inquiry-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                      <SelectContent>
                        {/* Rentals → show all 4 specific facilities. Birthday/Event → only the 2 venues. */}
                        {form.inquiry_type === "rental" ? (
                          <>
                            <SelectItem value="rrdc_field">Rolling River — Field</SelectItem>
                            <SelectItem value="rrdc_rink">Rolling River — Hockey Rink</SelectItem>
                            <SelectItem value="rrdc_courts">Rolling River — Basketball Courts</SelectItem>
                            <SelectItem value="turf_house">Turf House</SelectItem>
                          </>
                        ) : (
                          <>
                            <SelectItem value="rrdc_field">Rolling River</SelectItem>
                            <SelectItem value="turf_house">Turf House</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Location</label>
                    <Input value="Rolling River" disabled className="bg-muted" />
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">First Name *</label>
                  <Input data-testid="inq-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Last Name *</label>
                  <Input data-testid="inq-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
                </div>
              </div>

              {/* Organization - shown for rentals */}
              {(form.inquiry_type === "rental" || form.inquiry_type === "picnic") && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Organization / Team Name <span className="text-muted-foreground text-xs">(optional)</span></label>
                  <Input data-testid="inq-organization" value={form.organization || ""} onChange={(e) => set("organization", e.target.value)} placeholder="e.g. East Side Soccer Club" />
                </div>
              )}

              <AddressAutocomplete
                street={form.street}
                city={form.city}
                state={form.state}
                zip={form.zip_code}
                testIdPrefix="inq"
                onChange={({ street, city, state, zip }) => setForm(prev => ({ ...prev, street, city, state, zip_code: zip }))}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Phone *</label>
                  <Input data-testid="inq-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Email *</label>
                  <Input data-testid="inq-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
              </div>

              {/* Conditional Fields - Rental */}
              {form.inquiry_type === "rental" && (
                <div className="space-y-4 p-4 rounded-lg border border-border bg-muted/30">
                  <h3 className="text-sm font-semibold">Rental Details</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: "athletic", label: "Athletic Rental", desc: "Sports practice, league, training" },
                      { key: "other", label: "Other Rental", desc: "Photo / video shoot, misc" },
                    ].map((sub) => {
                      const active = form.rental_subtype === sub.key;
                      return (
                        <button
                          key={sub.key}
                          type="button"
                          data-testid={`rental-subtype-${sub.key}`}
                          onClick={() => set("rental_subtype", sub.key)}
                          className={`text-left rounded-lg border p-3 transition-all ${
                            active
                              ? "border-blue-500 bg-blue-500/10 ring-2 ring-blue-500/30"
                              : "border-border bg-background hover:bg-muted/50"
                          }`}
                        >
                          <p className={`text-sm font-semibold ${active ? "text-blue-600 dark:text-blue-400" : "text-foreground"}`}>{sub.label}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{sub.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                  {form.rental_subtype === "athletic" && (
                    <>
                      <Input data-testid="inq-sport" placeholder="Sport or activity" value={form.sport_activity} onChange={(e) => set("sport_activity", e.target.value)} />
                      <Input data-testid="inq-players" type="number" placeholder="Number of players" value={form.num_players} onChange={(e) => set("num_players", e.target.value)} />
                      <p className="text-xs text-muted-foreground">Note: No equipment provided. Renters must bring their own.</p>
                    </>
                  )}
                  {form.rental_subtype === "other" && (
                    <>
                      <Input data-testid="inq-other-purpose" placeholder="Purpose (e.g. photo shoot, video shoot, corporate filming)" value={form.sport_activity} onChange={(e) => set("sport_activity", e.target.value)} />
                      <Input data-testid="inq-other-people" type="number" placeholder="Number of people on site" value={form.num_players} onChange={(e) => set("num_players", e.target.value)} />
                    </>
                  )}
                </div>
              )}

              {/* Conditional Fields - Birthday */}
              {form.inquiry_type === "birthday_party" && (
                <div className="space-y-4 p-4 rounded-lg border border-border bg-muted/30">
                  <h3 className="text-sm font-semibold">Birthday Party Details</h3>

                  {/* Guest count disclaimer */}
                  <div className="rounded-lg border border-blue-300 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/5 p-3 sm:p-4" data-testid="birthday-guest-disclaimer">
                    <p className="text-xs sm:text-sm text-blue-900 dark:text-blue-100 leading-relaxed">
                      <span className="font-semibold">Enrolled campers</span> party includes up to <span className="font-semibold">25 children</span>. All other guests include up to <span className="font-semibold">15 children</span>.
                    </p>
                  </div>

                  {/* Amount of kids — moved above packages so attendees are known first */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input data-testid="inq-guests" type="number" placeholder="Number of guests" value={form.num_guests} onChange={(e) => set("num_guests", e.target.value)} />
                    <Input data-testid="inq-child-age" type="number" placeholder="Age of birthday child" value={form.child_age} onChange={(e) => set("child_age", e.target.value)} />
                  </div>

                  {/* What every party includes — between guest count and package picker */}
                  <div className="rounded-lg border border-muted bg-muted/30 p-3 sm:p-4 text-center" data-testid="birthday-party-includes">
                    <p className="text-xs sm:text-sm text-foreground leading-relaxed">
                      Each party includes two 30-minute camp activities and 30 minutes for pizza and cake, plus five pizzas, paper goods, invitations, favors, and 2–3 event staff handling setup and cleanup. Optional upgrades include extra guests, extended time, catering, décor, photography, or videography. For pricing and availability, choose a package below.
                    </p>
                  </div>

                  {/* Tier picker */}
                  <div className="grid grid-cols-2 gap-3">
                    {["standard", "premium"].map((tierKey) => {
                      const label = tierKey === "premium" ? "Premium Packages" : "Party Packages";
                      const capacity = tierKey === "premium"
                        ? "Up to 20 guests · 25 for enrolled campers"
                        : "Up to 15 guests · 25 for enrolled campers";
                      const active = form.package_tier === tierKey;
                      return (
                        <button
                          key={tierKey}
                          type="button"
                          data-testid={`tier-${tierKey}`}
                          onClick={() => {
                            const newTier = tierKey === form.package_tier ? "" : tierKey;
                            setForm({ ...form, package_tier: newTier, venue_option: "" });
                          }}
                          className={`text-left rounded-lg border p-4 transition-all ${
                            active
                              ? "border-blue-500 bg-blue-500/10 ring-2 ring-blue-500/30"
                              : "border-border bg-background hover:bg-muted/50"
                          }`}
                        >
                          <p className={`text-sm font-semibold ${active ? "text-blue-600 dark:text-blue-400" : "text-foreground"}`}>{label}</p>
                          <p className="text-xs text-muted-foreground mt-1">{capacity}</p>
                        </button>
                      );
                    })}
                  </div>

                  {/* Package option list with bullets — pulled live from /api/venues so admin price/bullet edits show up */}
                  {form.package_tier && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Choose a package</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {venues
                          .filter((v) => (v.tier || "standard") === form.package_tier)
                          .map((pkg) => {
                            const active = form.venue_option === pkg.name;
                            return (
                              <button
                                key={pkg.id}
                                type="button"
                                data-testid={`pkg-${pkg.name.replace(/\s+/g, "-").toLowerCase()}`}
                                onClick={() => set("venue_option", pkg.name)}
                                className={`text-left rounded-lg border p-3 transition-all ${
                                  active
                                    ? "border-blue-500 bg-blue-500/10 ring-2 ring-blue-500/30"
                                    : "border-border bg-background hover:bg-muted/50"
                                }`}
                              >
                                <div className="flex items-baseline justify-between gap-2">
                                  <p className={`text-sm font-semibold ${active ? "text-blue-600 dark:text-blue-400" : "text-foreground"}`}>{pkg.name}</p>
                                  {pkg.age_note && (
                                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{pkg.age_note}</span>
                                  )}
                                </div>
                                {pkg.bullets && pkg.bullets.length > 0 ? (
                                  <ul className="mt-2 space-y-1">
                                    {pkg.bullets.map((b) => (
                                      <li key={b} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                        <span className="text-blue-500 mt-0.5">•</span>
                                        <span>{b}</span>
                                      </li>
                                    ))}
                                  </ul>
                                ) : pkg.description ? (
                                  <p className="mt-2 text-xs text-muted-foreground">{pkg.description}</p>
                                ) : null}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Conditional Fields - Event */}
              {form.inquiry_type === "event" && (
                <div className="space-y-4 p-4 rounded-lg border border-border bg-muted/30">
                  <h3 className="text-sm font-semibold">Event Details</h3>
                  <Input data-testid="inq-event-type" placeholder="Type of event" value={form.event_type} onChange={(e) => set("event_type", e.target.value)} />
                  <Input data-testid="inq-attendance" type="number" placeholder="Expected attendance" value={form.expected_attendance} onChange={(e) => set("expected_attendance", e.target.value)} />
                  <Input data-testid="inq-catering" placeholder="Catering needs" value={form.catering_needs} onChange={(e) => set("catering_needs", e.target.value)} />
                </div>
              )}

              {/* Conditional Fields - Picnic */}
              {form.inquiry_type === "picnic" && (
                <div className="space-y-4 p-4 rounded-lg border border-border bg-muted/30">
                  <h3 className="text-sm font-semibold">Picnic Details</h3>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Picnic Type</label>
                    <Select value={form.picnic_type} onValueChange={(v) => set("picnic_type", v)}>
                      <SelectTrigger data-testid="inq-picnic-type"><SelectValue placeholder="Select picnic type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="corporate">Corporate Picnic</SelectItem>
                        <SelectItem value="school_picnic">School Picnic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Input data-testid="inq-picnic-attendance" type="number" placeholder="Expected attendance" value={form.expected_attendance} onChange={(e) => set("expected_attendance", e.target.value)} />
                  <Input data-testid="inq-picnic-catering" placeholder="Catering needs" value={form.catering_needs} onChange={(e) => set("catering_needs", e.target.value)} />
                </div>
              )}

              {/* Date / Time */}
              {form.inquiry_type && form.inquiry_type !== "event" && (
                <div className={`grid grid-cols-1 ${form.inquiry_type === "birthday_party" ? "sm:grid-cols-2" : "sm:grid-cols-3"} gap-4`}>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Preferred Date</label>
                    <Input data-testid="inq-date" type="date" value={form.requested_date} onChange={(e) => set("requested_date", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">
                      {form.inquiry_type === "birthday_party" && form.location === "rrdc_field" ? "Time Slot" : "Start Time"}
                    </label>
                    {form.inquiry_type === "birthday_party" && form.location === "rrdc_field" ? (
                      <Select value={form.start_time} onValueChange={(v) => set("start_time", v)}>
                        <SelectTrigger data-testid="inq-time-slot">
                          <SelectValue placeholder="Select time slot" />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            { value: "11:00", label: "11:00 AM - 12:30 PM" },
                            { value: "14:00", label: "2:00 PM - 3:30 PM" },
                          ].map((slot) => {
                            const slotEnd = addHoursToTime(slot.value, 1.5);
                            const taken = rangeConflicts(slot.value, slotEnd);
                            return (
                              <SelectItem key={slot.value} value={slot.value} disabled={taken}>
                                {slot.label} {taken ? "— Unavailable" : ""}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input data-testid="inq-time" type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} />
                    )}
                  </div>
                  {form.inquiry_type !== "birthday_party" && (
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium">Duration (hours)</label>
                      <Input data-testid="inq-duration" type="number" min="1" step="0.5" value={form.duration} onChange={(e) => set("duration", e.target.value)} />
                    </div>
                  )}
                  {form.inquiry_type === "birthday_party" && (
                    <p className="text-xs text-muted-foreground sm:col-span-2 mt-1">Birthday parties are 90-minute blocks.</p>
                  )}
                </div>
              )}

              {hasTimeConflict && (
                <div data-testid="inq-conflict-banner" className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400 flex items-start gap-2">
                  <span className="font-medium">Please select a different time.</span>
                </div>
              )}

              {form.inquiry_type === "event" && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Preferred Date</label>
                  <Input data-testid="inq-event-date" type="date" value={form.requested_date} onChange={(e) => set("requested_date", e.target.value)} />
                </div>
              )}

              {/* Lead Source */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium">How did you hear about us?</label>
                <Select value={form.lead_source} onValueChange={(v) => set("lead_source", v)}>
                  <SelectTrigger data-testid="inq-source"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Google">Google</SelectItem>
                    <SelectItem value="Referral">Referral</SelectItem>
                    <SelectItem value="Social Media">Social Media</SelectItem>
                    <SelectItem value="Drive-by">Drive-by</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Additional Notes</label>
                <textarea
                  data-testid="inq-notes"
                  className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[80px]"
                  value={form.additional_notes}
                  onChange={(e) => set("additional_notes", e.target.value)}
                  placeholder="Any additional details..."
                />
              </div>

              {/* Weather disclaimer for outdoor */}
              {form.location === "rrdc_field" && (
                <p className="text-xs text-amber-500 bg-amber-500/10 rounded-lg p-3">
                  Outdoor bookings may be subject to weather cancellation or reschedule.
                </p>
              )}

              <Button data-testid="inquiry-submit-btn" type="submit" className="w-full" disabled={loading || hasTimeConflict}>
                {loading ? "Submitting..." : hasTimeConflict ? "Select a different time" : "Submit Inquiry"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
